class ConfirmationTimeoutError extends Error {}

export class MembershipCheckout {
  constructor({ purchases, errorCodes, element, onConfirming, onSuccess, onError }) {
    this.purchases = purchases;
    this.errorCodes = errorCodes;
    this.element = element;
    this.onConfirming = onConfirming;
    this.onSuccess = onSuccess;
    this.onError = onError;
    this.state = "idle";
    this.mounted = false;
    this.history = new CheckoutHistory();
    this.observer = new MutationObserver(() => this.checkoutChanged());
  }

  async start(options) {
    if (this.state !== "idle") throw new Error("Checkout can start only once.");
    this.state = "opening";
    try {
      this.observer.observe(this.element, { childList: true });
      this.history.start();
      const purchase = this.purchases.purchase(options);
      purchase.then(
        (result) => {
          if (this.state === "pending") this.succeed(result);
        },
        () => {}
      );
      const interrupted = new Promise((_, reject) => {
        this.interrupt = reject;
      });
      const result = await Promise.race([purchase, interrupted]);
      this.succeed(result);
    } catch (error) {
      if (this.state !== "disposed") {
        this.state = this.errorState(error);
        this.onError?.(error);
      }
    } finally {
      await this.finish();
    }
  }

  succeed(result) {
    if (this.state === "disposed") return;
    this.state = "succeeded";
    this.onSuccess?.(result);
  }

  checkoutChanged() {
    if (this.element.childElementCount) {
      this.mounted = true;
      this.history.capture();
      if (this.state === "closing") void this.history.pop();
      else if (this.state === "opening") this.state = "open";
    } else if (this.state === "open") {
      // The SDK unmounts before fetching customer information and exposes no callback for that interval.
      this.state = "confirming";
      this.confirmationTimer = setTimeout(() => this.interrupt(new ConfirmationTimeoutError()), 15000);
      this.onConfirming?.();
    }
  }

  cancel() {
    if (!["opening", "open"].includes(this.state)) return;
    this.state = "closing";
    if (this.mounted) void this.history.pop();
  }

  dispose() {
    this.state = "disposed";
    this.onConfirming = null;
    this.onSuccess = null;
    this.onError = null;
    this.interrupt?.(new DOMException("Checkout was disconnected.", "AbortError"));
    return this.finish();
  }

  finish() {
    this.observer.disconnect();
    clearTimeout(this.confirmationTimer);
    return this.history.finish();
  }

  errorState(error) {
    if (error.errorCode === this.errorCodes.UserCancelledError) return "canceled";
    if (
      error.errorCode === this.errorCodes.PaymentPendingError ||
      error.errorCode === this.errorCodes.ProductAlreadyPurchasedError ||
      error instanceof ConfirmationTimeoutError
    )
      return "pending";
    return "failed";
  }
}

export class CheckoutHistory {
  constructor() {
    this.id = crypto.randomUUID();
    this.url = window.location.href;
    this.originalState = window.history.state;
    this.turbo = window.Turbo?.session?.history;
  }

  start() {
    // RevenueCat's checkout owns a history entry, so Turbo must not restore the page when that entry closes.
    this.resumeTurbo = this.turbo?.started;
    if (this.resumeTurbo) this.turbo.stop();
  }

  capture() {
    if (window.location.href !== this.url || !window.history.state?.checkoutOpen) return;
    if (window.history.state.peptideCheckout && window.history.state.peptideCheckout !== this.id) return;
    window.history.replaceState({ ...this.originalState, ...window.history.state, peptideCheckout: this.id }, "");
  }

  get current() {
    return window.location.href === this.url && window.history.state?.peptideCheckout === this.id;
  }

  pop() {
    if (this.popping) return this.popping;
    this.capture();
    if (!this.current) return Promise.resolve();
    window.history.replaceState(this.originalState, "");
    this.popping = new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        window.removeEventListener("popstate", done);
        resolve();
      };
      const timer = setTimeout(() => {
        window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
        done();
      }, 1500);
      window.addEventListener("popstate", done, { once: true });
      window.history.back();
    });
    return this.popping;
  }

  finish() {
    this.finishing ||= this.pop().finally(() => {
      if (this.resumeTurbo) this.turbo.start();
    });
    return this.finishing;
  }
}
